import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarClock } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/hooks/useSeo";
import { SERVICES } from "@/lib/data";

const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";
const VP_20 = { once: true, amount: 0.2 };

const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: (i = 0) => ({ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Services() {
  useSeo({
    title: "Services — Métis Tech Labs",
    description:
      "AI Automation, Custom Software Development, Intelligent Websites, Business Dashboards, AI Agents, CRM & Business Systems, API & Workflow Integrations, and Ongoing Support & Optimization.",
  });

  return (
    <div className="pt-32">
      <section className={container}>
        <SectionHeader
          eyebrow="Services"
          title="Technology that solves business problems"
          subtitle="Eight core capabilities, each engineered to deliver a clear, measurable business outcome."
        />

        <div className="mt-14 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={VP_20}
            >
              <GlassCard
                glow={i % 2 ? "purple" : "green"}
                className="group flex h-full flex-col p-6"
                data-testid={`services-card-${s.id}`}
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl glass text-neon-g transition-transform duration-500 group-hover:rotate-6">
                  <s.icon size={22} />
                </div>
                <span className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full border border-[color:var(--stroke-g)] bg-[rgba(57,255,20,0.06)] px-2.5 py-1 text-[11px] font-medium text-neon-g">
                  {s.benefit}
                </span>
                <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-brand">{s.desc}</p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-neon-g transition-colors hover:text-white"
                  data-testid={`services-consult-${s.id}`}
                >
                  <CalendarClock size={14} /> Schedule a Consultation
                </Link>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        <div className="mt-16">
          <div className="neon-frame relative overflow-hidden rounded-3xl glass-strong px-6 py-12 text-center sm:px-12">
            <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
              Not sure where to start?
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-brand">
              Tell us your goal. We’ll recommend the highest-leverage system to build first.
            </p>
            <div className="mt-7 flex justify-center">
              <GlowButton as={Link} to="/contact" pulse data-testid="services-cta-button">
                Schedule a Consultation <ArrowUpRight size={16} />
              </GlowButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
